import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link, useNavigate} from 'react-router-dom';
import {Turnstile} from '@marsidev/react-turnstile';
import authService from '../../common/services/authService';
import {RegistrationRequest} from "../../common/types/Auth";
import {handleError} from "../../common/utils/errorHandling";
import {getRuntimeConfig} from "../../common/services/runtimeConfig";
import {
  CenteredButton,
  CenteredCheckbox,
  CenteredForm,
  CenteredHeader,
  CenteredInfo,
  CenteredInputPassword,
  CenteredInputText,
  CenteredLayout
} from '../../layout/centered/components';
import InlineErrorMessage from '../../common/components/InlineErrorMessage';

const RegisterInner: React.FC = () => {
  const {t} = useTranslation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: ''
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {name, value} = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError('');

    if (!termsAccepted) {
      setError(t('common.pages.register.termsError'));
      return;
    }

    if (!captchaToken) {
      setError(t('common.pages.register.captchaError'));
      return;
    }

    await handleError(
      async () => {

        const request: RegistrationRequest = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          captchaToken
        };

        await authService.register(request);
        navigate('/registration-confirmation');
      },
      'Registration failed:',
      (err: any) => {
        const backendError = err?.response?.data?.error || err?.response?.data?.message;
        setError(backendError || t('common.pages.register.errorFailed'));
      }
    );
  };

  return (
    <CenteredLayout sectionId="registration-section">
      <CenteredHeader
        title={t('common.pages.register.title')}
        subtitle={t('common.pages.register.subtitle')}
      />

      <CenteredForm onSubmit={handleSubmit}>

        <CenteredInputText
          id="first_name"
          name="firstName"
          type="text"
          value={formData.firstName}
          onChange={handleInputChange}
          placeholder={t('common.pages.register.firstNamePlaceholder')}
          label={t('common.pages.register.firstName')}
          icon="fa fa-user"
          required
        />

        <CenteredInputText
          id="last_name"
          name="lastName"
          type="text"
          value={formData.lastName}
          onChange={handleInputChange}
          placeholder={t('common.pages.register.lastNamePlaceholder')}
          label={t('common.pages.register.lastName')}
          icon="fa fa-user"
          required
        />

        <CenteredInputText
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          placeholder={t('common.pages.register.emailPlaceholder')}
          label={t('common.pages.register.email')}
          icon="fa fa-envelope"
          required
        />

        <CenteredInputPassword
          id="password"
          name="password"
          value={formData.password}
          onChange={handleInputChange}
          placeholder={t('common.pages.register.passwordPlaceholder')}
          label={t('common.pages.register.password')}
          icon="fa fa-lock"
          feedback={false}
          required
          helpText={t('common.pages.register.passwordHelp')}
        />

        <CenteredCheckbox
          id="terms"
          name="terms"
          checked={termsAccepted}
          onChange={(e) => {
            setTermsAccepted(e.target.checked);
            if (e.target.checked) {
              setError('');
            }
          }}
        >
          {t('common.pages.register.termsAgree')} <Link to="/terms-of-service"
                                                        className="text-gray-800 hover:underline cursor-pointer font-medium">{t('common.pages.register.termsOfService')}</Link>&nbsp;
          {t('common.pages.register.and')} <Link to="/privacy-policy"
                                                 className="text-gray-800 hover:underline cursor-pointer font-medium">{t('common.pages.register.privacyPolicy')}</Link>
        </CenteredCheckbox>

        <InlineErrorMessage message={error}/>

        <Turnstile
          siteKey={getRuntimeConfig().TURNSTILE_SITE_KEY}
          options={{
            size: 'invisible',
            action: 'register',
          }}
          onSuccess={(token) => setCaptchaToken(token)}
        />

        <CenteredButton
          type="submit"
          label={t('common.pages.register.signUp')}
          icon="fa fa-user-plus"
          iconPos="left"
        />

      </CenteredForm>

      <CenteredInfo className="mt-8">
        {t('common.pages.register.haveAccount')}&nbsp;
        <Link
          className="text-gray-800 hover:underline cursor-pointer font-medium"
          to={'/login'}
        >
          {t('common.pages.register.signInHere')}
        </Link>
      </CenteredInfo>

      <CenteredInfo variant="small" className="mt-6">
        {t('common.pages.register.benefitText')}
      </CenteredInfo>
    </CenteredLayout>
  );
};

const Register: React.FC = () => {
  return (
    <RegisterInner/>
  );
};

export default Register;
